import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SuperAuthProvider, useSuperAuth } from "./context/SuperAuthContext";
import { Toaster } from "sonner";
import Landing from "./pages/Landing";
import EntrarPage from "./pages/EntrarPage";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import ReceptionDashboard from "./pages/ReceptionDashboard";
import ProfessionalsPage from "./pages/ProfessionalsPage";
import AdminsPage from "./pages/AdminsPage";
import CategoriesPage from "./pages/CategoriesPage";
import HistoryPage from "./pages/HistoryPage";
import ReportsPage from "./pages/ReportsPage";
import ProfessionalHome from "./pages/ProfessionalHome";
import MyStatus from "./pages/MyStatus";
import TVPanel from "./pages/TVPanel";
import PersonalizacaoPage from "./pages/PersonalizacaoPage";
import SuperLogin from "./pages/super/SuperLogin";
import SuperLayout from "./pages/super/SuperLayout";
import SuperTenants from "./pages/super/SuperTenants";
import SuperTenantCreate from "./pages/super/SuperTenantCreate";
import SuperTenantEdit from "./pages/super/SuperTenantEdit";
import SuperMyAccount from "./pages/super/SuperMyAccount";
import SupportEnter from "./pages/SupportEnter";
import DocumentTitle from "./components/DocumentTitle";
import TenantManifest from "./components/TenantManifest";

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-sm uppercase tracking-widest text-gray-400">Carregando…</div>
    </div>
  );
}

/**
 * Guarda de rota de tenant: garante que o slug da URL confere com o tenant do
 * usuário autenticado. Se não confere, redireciona para /<userSlug>/fila (nunca
 * exibe dados do outro tenant, mesmo que a URL diga o contrário).
 */
function TenantScopedRoute({ children, role, level }) {
  const { user, loading, tenant } = useAuth();
  const { tenantSlug } = useParams();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to={`/${tenantSlug}/login`} replace state={{ from: location }} />;
  // isolamento: URL manipulada → volta para o próprio tenant
  if (tenant && tenantSlug && tenant.slug !== tenantSlug) {
    return <Navigate to={`/${tenant.slug}/fila`} replace />;
  }
  if (role && user.role !== role) {
    return <Navigate to={`/${tenantSlug}/${user.role === "admin" ? "fila" : "minha-vez"}`} replace />;
  }
  if (level && user.admin_level !== level) {
    return <Navigate to={`/${tenantSlug}/fila`} replace />;
  }
  return children;
}

function TenantEntry() {
  const { tenantSlug } = useParams();
  const { user, loading, tenant } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to={`/${tenantSlug}/login`} replace />;
  if (tenant && tenant.slug !== tenantSlug) return <Navigate to={`/${tenant.slug}/fila`} replace />;
  return <Navigate to={`/${tenantSlug}/${user.role === "admin" ? "fila" : "minha-vez"}`} replace />;
}

function SuperProtected({ children }) {
  const { sa, loading } = useSuperAuth();
  if (loading) return <Loading />;
  if (!sa) return <Navigate to="/super/login" replace />;
  return children;
}

/**
 * Redirect global de rotas legadas (/login, /fila, /tv...) para o tenant do
 * usuário autenticado — mantém compatibilidade se alguém acessar link antigo.
 */
function LegacyRedirect({ to = "" }) {
  const { user, loading, tenant } = useAuth();
  if (loading) return <Loading />;
  if (user && tenant) return <Navigate to={`/${tenant.slug}${to || `/${user.role === "admin" ? "fila" : "minha-vez"}`}`} replace />;
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SuperAuthProvider>
          <BrowserRouter>
            <DocumentTitle />
            <TenantManifest />
            <Routes>
              {/* Landing institucional */}
              <Route path="/" element={<Landing />} />
              <Route path="/entrar" element={<EntrarPage />} />
              <Route path="/support/enter" element={<SupportEnter />} />

              {/* Super Admin — global */}
              <Route path="/super/login" element={<SuperLogin />} />
              <Route element={<SuperProtected><SuperLayout /></SuperProtected>}>
                <Route path="/super" element={<Navigate to="/super/clientes" replace />} />
                <Route path="/super/clientes" element={<SuperTenants />} />
                <Route path="/super/clientes/novo" element={<SuperTenantCreate />} />
                <Route path="/super/clientes/:id" element={<SuperTenantEdit />} />
                <Route path="/super/minha-conta" element={<SuperMyAccount />} />
              </Route>

              {/* Legacy redirects (compat com URLs antigas sem slug) */}
              <Route path="/login" element={<LegacyRedirect to="/login" />} />
              <Route path="/fila" element={<LegacyRedirect to="/fila" />} />
              <Route path="/tv" element={<LegacyRedirect to="/tv" />} />
              <Route path="/relatorios" element={<LegacyRedirect to="/relatorios" />} />
              <Route path="/historico" element={<LegacyRedirect to="/relatorios" />} />
              <Route path="/profissionais" element={<LegacyRedirect to="/profissionais" />} />
              <Route path="/recepcao" element={<LegacyRedirect to="/recepcao" />} />
              <Route path="/configuracoes" element={<LegacyRedirect to="/configuracoes" />} />
              <Route path="/personalizacao" element={<LegacyRedirect to="/personalizacao" />} />
              <Route path="/minha-vez" element={<LegacyRedirect to="/minha-vez" />} />
              <Route path="/meu-status" element={<LegacyRedirect to="/meu-status" />} />

              {/* Tenant path-based */}
              <Route path="/:tenantSlug" element={<TenantEntry />} />
              <Route path="/:tenantSlug/login" element={<Login />} />
              <Route path="/:tenantSlug/tv" element={<TenantScopedRoute role="admin"><TVPanel /></TenantScopedRoute>} />
              <Route element={<TenantScopedRoute><Layout /></TenantScopedRoute>}>
                <Route path="/:tenantSlug/fila" element={<TenantScopedRoute role="admin"><ReceptionDashboard /></TenantScopedRoute>} />
                <Route path="/:tenantSlug/profissionais" element={<TenantScopedRoute role="admin"><ProfessionalsPage /></TenantScopedRoute>} />
                <Route path="/:tenantSlug/recepcao" element={<TenantScopedRoute role="admin" level="master"><AdminsPage /></TenantScopedRoute>} />
                <Route path="/:tenantSlug/configuracoes" element={<TenantScopedRoute role="admin"><CategoriesPage /></TenantScopedRoute>} />
                <Route path="/:tenantSlug/personalizacao" element={<TenantScopedRoute role="admin" level="master"><PersonalizacaoPage /></TenantScopedRoute>} />
                <Route path="/:tenantSlug/relatorios" element={<TenantScopedRoute><ReportsPage /></TenantScopedRoute>} />
                <Route path="/:tenantSlug/historico" element={<Navigate to="../relatorios" replace />} />
                <Route path="/:tenantSlug/minha-vez" element={<TenantScopedRoute role="professional"><ProfessionalHome /></TenantScopedRoute>} />
                <Route path="/:tenantSlug/meu-status" element={<TenantScopedRoute role="professional"><MyStatus /></TenantScopedRoute>} />
              </Route>

              {/* Catch-all → landing */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </SuperAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
